import { DbEnum } from "../../../generated/prisma-enums";
import type { DbEntity } from "../../../generated/prisma-models";

// Define enums locally to avoid Prisma runtime import
const BillingMethodType = {
  BALANCE: "BALANCE",
  CARD: "CARD"
} as const;

import { z } from "zod";

// ------ Clerk Organization Roles ------

/**
 * Clerk organization role constants.
 * These are NOT Prisma enums — they come from Clerk's JWT and user metadata.
 *
 * - OrgRole.ADMIN  → full access, bypasses deployment permission checks
 * - OrgRole.MEMBER → standard member, subject to per-deployment permissions
 *
 * Rule: never compare against `"org:admin"` as a string literal.
 *       Always use `OrgRole.ADMIN`, `OrgRole.MEMBER`.
 *
 * @example
 * if (userRole === OrgRole.ADMIN) { ... }          // ✅
 * if (userRole !== "org:admin") { ... }             // ❌ hardcoded string
 * z.nativeEnum(OrgRole)                             // ✅ Zod integration
 */
export const OrgRole = {
  ADMIN: "org:admin",
  MEMBER: "org:member"
} as const;
export type OrgRole = (typeof OrgRole)[keyof typeof OrgRole];

// ------ UI Preferences ------

/**
 * View mode for list displays
 */
export const ViewMode = {
  CARDS: "cards",
  LIST: "list"
} as const;

export type ViewMode = (typeof ViewMode)[keyof typeof ViewMode];

/**
 * Feature identifiers for UI preferences
 * Each feature can have its own set of preferences
 */
export const UiFeature = {
  SKILLS: "skills",
  WORKFLOWS: "workflows",
  DOCUMENTS: "documents",
  AGENTS: "agents",
  DEPLOYMENTS: "deployments",
  PLUGINS: "plugins",
  CREDENTIALS: "credentials",
  /** The Vibe apps console (`/app/vibe`) — cards vs list density. */
  VIBE_APPS: "vibe-apps"
} as const;

export type UiFeature = (typeof UiFeature)[keyof typeof UiFeature];

/**
 * Per-feature preferences schema
 * Each feature can have viewMode and additional settings
 */
export const featurePreferencesSchema = z.object({
  viewMode: z.enum(["cards", "list"]).optional(),
  listPreferences: z
    .object({
      pageSize: z.number().optional(),
      sortField: z.string().optional(),
      sortOrder: z.enum(["asc", "desc"]).optional(),
      groupBy: z.string().optional(),
      filters: z.record(z.string(), z.string()).optional()
    })
    .optional()
});

export type FeaturePreferences = z.infer<typeof featurePreferencesSchema>;

/**
 * Global UI preferences (not tied to a specific feature)
 */
export const globalPreferencesSchema = z.object({
  /** User's favorite/default agent ID */
  favoriteAgentId: z.string().nullable().optional(),
  /** Whether real-time sync via WebSocket is enabled. Defaults to false (off). */
  realtimeSyncEnabled: z.boolean().optional()
});

export type GlobalPreferences = z.infer<typeof globalPreferencesSchema>;

/**
 * User UI preferences schema - stored as JSON on User model
 * Structure: { global: {...}, features: { skills: {...}, workflows: {...} } }
 */
export const uiPreferencesSchema = z.object({
  /** Global preferences not tied to a specific feature */
  global: globalPreferencesSchema.optional(),
  /** Per-feature preferences keyed by feature identifier */
  features: z.record(z.string(), featurePreferencesSchema).optional()
});

export type UiPreferences = z.infer<typeof uiPreferencesSchema>;

/**
 * Default UI preferences for new users
 */
export const defaultUiPreferences: UiPreferences = {
  global: {
    favoriteAgentId: null,
    realtimeSyncEnabled: false
  },
  features: {}
};

/**
 * Helper to get feature preferences with defaults
 */
export const getFeaturePreferences = (
  prefs: UiPreferences | null | undefined,
  feature: UiFeature
): FeaturePreferences => {
  return prefs?.features?.[feature] ?? { viewMode: "cards" };
};

/**
 * Helper to get view mode for a feature with default fallback
 */
export const getFeatureViewMode = (
  prefs: UiPreferences | null | undefined,
  feature: UiFeature
): ViewMode => {
  return prefs?.features?.[feature]?.viewMode ?? "cards";
};
// ------ Users ------

export const createUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  firstName: z.string().optional(),
  lastName: z.string().optional()
});

export type CreateUserSchemaType = z.infer<typeof createUserSchema>;

// ------ User Credentials ------

export enum OpenAIModel {
  "GPT-4" = "gpt-4o",
  "GPT-3.5-TURBO" = "gpt-3.5-turbo",
  "GPT-4o" = "gpt-4o",
  "GPT-4o-mini" = "gpt-4o-mini",
  "GPT-4.1" = "gpt-4.1"
}

export const createOrUpdateCredentialSchema = z.object({
  // Non-empty guard: an empty API key or model is never a valid credential.
  // Mirrors the controller's former `Missing credential fields` check.
  key: z.string().min(1),
  provider: z.nativeEnum(DbEnum.CredentialProvider),
  model: z.string().min(1)
});

export const updateBillingMethodSchema = z.object({
  method: z.nativeEnum(BillingMethodType).optional().describe("The new billing method"),
  model: z.string().optional().describe("The new model")
});

export const userCredentialBaseSchema = z.object({
  key: z.string(),
  model: z.string()
});

export type CreateOrUpdateCredential = z.infer<typeof createOrUpdateCredentialSchema>;

export type UpdateBillingMethod = z.infer<typeof updateBillingMethodSchema>;

export type UserCredentialBase = z.infer<typeof userCredentialBaseSchema>;

/**
 * The user fields a chat member carries to a client: who the member is, and no
 * more. The whole `User` row — phone, tracking, UI preferences, onboarding state
 * — is not a chat member's business, and `GET /api/chatrooms/:chatRoomId/members`
 * is reachable by API key. The backend's select is checked against these keys,
 * so the two cannot drift.
 */
export type ChatMemberUser = Pick<DbEntity.User, "id" | "email" | "firstName" | "lastName">;

export type ChatUserMemberWithUserDetails = DbEntity.ChatUserMember & {
  user: ChatMemberUser;
};

export const saveTokenUsageSchema = z.object({
  messageId: z
    .string()
    .optional()
    .describe("The message id from the chat platform if the usage is related to a message"),
  documentId: z
    .string()
    .optional()
    .describe("The document id if the usage is related to a document"),
  agentId: z.string().optional(),
  tokenTotalCount: z.number(),
  model: z.string()
});

export const webhookSettingsSchema = z.object({
  url: z.string().describe("The the webhook url to send the request to"),
  headerKey: z.string().describe("The header key to use for the request"),
  headerValue: z.string().describe("The header value to use for the request")
});
